import React from "react";
import { Typography, Container, Grid, Box, IconButton } from "@mui/material";
import { Link } from "react-router-dom";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import YouTubeIcon from "@mui/icons-material/YouTube";
import { getAllCities } from "../data/cityData";
import { getAllContinents } from "../data/locationData";

const TikTokIcon = ({ fontSize = "inherit" }) => (
  <svg fill="currentColor" viewBox="0 0 24 24" width={fontSize === "inherit" ? "1em" : 24} height={fontSize === "inherit" ? "1em" : 24}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1 0-5.78c.27 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15.2a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.71a8.2 8.2 0 0 0 4.76 1.52V6.77a4.83 4.83 0 0 1-1-.08z" />
  </svg>
);

const Footer = () => {
  const allCities = getAllCities();
  const allContinents = getAllContinents();

  return (
    <Box sx={{ flexGrow: 1 }}>
      <footer style={{ backgroundColor: "#1a0a2e", color: "white", padding: "40px 20px 20px" }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} sx={{ mb: 4 }} justifyContent="space-between">
            <Grid item xs={12} md={5}>
              <Typography variant="h6" sx={{ color: '#ffd700', mb: 2, fontWeight: 'bold' }}>
                Imperial Drummers
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8, lineHeight: 1.7 }}>
                UK's premier dhol entertainment company. Professional Punjabi dhol players
                for Asian weddings, Indian weddings, and cultural celebrations worldwide.
              </Typography>
            </Grid>

            <Grid item xs={6} md={3} sx={{ textAlign: { md: 'right' } }}>
              <Typography variant="h6" sx={{ color: '#ffd700', mb: 2, fontWeight: 'bold' }}>
                Worldwide
              </Typography>
              {allContinents.map((continent) => (
                <Link
                  key={continent.slug}
                  to={`/dhol-players/${continent.slug}`}
                  style={{
                    color: 'rgba(255,255,255,0.8)',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    display: 'block',
                    padding: '2px 0'
                  }}
                >
                  {continent.name}
                </Link>
              ))}
            </Grid>

            <Grid item xs={6} md={3} sx={{ textAlign: { md: 'right' } }}>
              <Typography variant="h6" sx={{ color: '#ffd700', mb: 2, fontWeight: 'bold' }}>
                Our Services
              </Typography>
              {[
                { name: "Wedding Entertainment", slug: "wedding-entertainment" },
                { name: "Corporate Events", slug: "corporate-events" },
                { name: "Cultural Festivals", slug: "cultural-festivals" },
                { name: "Media & Production", slug: "media-and-production" }
              ].map((svc) => (
                <Link
                  key={svc.slug}
                  to={`/services/${svc.slug}`}
                  style={{
                    color: 'rgba(255,255,255,0.8)',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    display: 'block',
                    padding: '2px 0'
                  }}
                >
                  {svc.name}
                </Link>
              ))}
            </Grid>
          </Grid>

          {/* SEO-only: full city link list, visually hidden but crawlable */}
          <nav
            aria-label="All service locations"
            style={{
              position: 'absolute',
              width: '1px',
              height: '1px',
              padding: 0,
              margin: '-1px',
              overflow: 'hidden',
              clip: 'rect(0, 0, 0, 0)',
              whiteSpace: 'nowrap',
              border: 0
            }}
          >
            {allCities.map((city) => (
              <Link key={city.slug} to={`/dhol-players/${city.slug}`}>
                Dhol Players {city.name}
              </Link>
            ))}
          </nav>

          <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.1)', pt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2 }}>
              <IconButton href="https://www.instagram.com/imperialdrummers" target="_blank" rel="noopener noreferrer" sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#ffd700' } }} aria-label="Instagram">
                <InstagramIcon />
              </IconButton>
              <IconButton href="https://www.tiktok.com/@imperialdrummers" target="_blank" rel="noopener noreferrer" sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#ffd700' } }} aria-label="TikTok">
                <TikTokIcon />
              </IconButton>
              <IconButton href="https://www.youtube.com/@imperialdrummers" target="_blank" rel="noopener noreferrer" sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#ffd700' } }} aria-label="YouTube">
                <YouTubeIcon />
              </IconButton>
              <IconButton href="https://www.facebook.com/imperialdrummers" target="_blank" rel="noopener noreferrer" sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#ffd700' } }} aria-label="Facebook">
                <FacebookIcon />
              </IconButton>
            </Box>
            <Typography variant="body2" sx={{ opacity: 0.7, textAlign: 'center' }}>
              © {new Date().getFullYear()} Imperial Drummers | All Rights Reserved |{' '}
              <Link to="/terms" style={{ color: 'rgba(255,255,255,0.7)' }}>Terms & Conditions</Link>
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 1.5 }}>
              <Box sx={{ width: 24, height: '1px', backgroundColor: 'rgba(255,255,255,0.2)' }} />
              <Typography variant="caption" sx={{ opacity: 0.55, letterSpacing: '0.08em', fontSize: '0.7rem' }}>
                Built by{' '}
                <a
                  href="https://www.async22.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: 'rgba(255,255,255,0.7)',
                    textDecoration: 'none',
                    fontWeight: 600,
                    letterSpacing: '0.05em',
                    transition: 'color 0.2s',
                  }}
                  onMouseEnter={e => e.target.style.color = '#ffd700'}
                  onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.7)'}
                >
                  Async22
                </a>
              </Typography>
              <Box sx={{ width: 24, height: '1px', backgroundColor: 'rgba(255,255,255,0.2)' }} />
            </Box>
          </Box>
        </Container>
      </footer>
    </Box>
  );
};

export default Footer;
